import React, { useState, useEffect } from 'react'
import { Users, Loader2 } from 'lucide-react'
import { Drawer, ImageUpload } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import usePartnerStore from '../../stores/partnerStore'
import usePartnerCategoryStore from '../../stores/partnerCategoryStore'
import usePartnerTierStore from '../../stores/partnerTierStore'
import { Partner, PartnerStatus } from '../../types/partner'
import { fileToBase64, getContentType } from '../../utils/imageUtils'
import { isValidUrl } from '../../utils/validation'
import { getPartnerLogoUrl } from '../../services/partnerService'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'

interface EditPartnerDrawerProps {
  isOpen: boolean
  onClose: () => void
  partner: Partner | null
}

const EditPartnerDrawer: React.FC<EditPartnerDrawerProps> = ({ isOpen, onClose, partner }) => {
  const { showSuccess, showError } = useToast()
  const { updatePartner, loadPartners } = usePartnerStore()
  const { categories, loadCategories } = usePartnerCategoryStore()
  const { tiers, loadTiers } = usePartnerTierStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [tierId, setTierId] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<PartnerStatus>('Active')
  const [isUpdating, setIsUpdating] = useState(false)

  // Logo state
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null)
  const [logoBase64, setLogoBase64] = useState<string | null>(null)
  const [logoFilename, setLogoFilename] = useState<string | null>(null)
  const [logoContentType, setLogoContentType] = useState<string | null>(null)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [logoRemoved, setLogoRemoved] = useState(false)

  // Load categories and tiers when drawer opens
  useEffect(() => {
    if (isOpen) {
      loadCategories(true) // Only load active categories
      loadTiers(true) // Only load active tiers
    }
  }, [isOpen, loadCategories, loadTiers])

  // Populate form when drawer opens or partner changes
  useEffect(() => {
    if (isOpen && partner) {
      setName(partner.name)
      setCategoryId(partner.category_id)
      setTierId(partner.tier_id || '')
      setWebsiteUrl(partner.website_url || '')
      setDescription(partner.description || '')
      setStatus(partner.status)
      setCurrentLogoUrl(partner.logo_url || null)
      // Reset new logo state
      setLogoBase64(null)
      setLogoFilename(null)
      setLogoContentType(null)
      setLogoPreviewUrl(null)
      setLogoRemoved(false)
    }
  }, [isOpen, partner])

  const resetForm = () => {
    setName('')
    setCategoryId('')
    setTierId('')
    setWebsiteUrl('')
    setDescription('')
    setStatus('Active')
    setCurrentLogoUrl(null)
    setLogoBase64(null)
    setLogoFilename(null)
    setLogoContentType(null)
    setLogoPreviewUrl(null)
    setLogoRemoved(false)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  // Handle logo upload - converts file to base64 for API
  const handleLogoUpload = async (file: File) => {
    setIsUploadingLogo(true)
    try {
      const base64 = await fileToBase64(file)
      const contentType = getContentType(file)

      setLogoBase64(base64)
      setLogoFilename(file.name)
      setLogoContentType(contentType)
      setLogoRemoved(false)
      // Create preview URL from base64 (must be set before this function returns)
      setLogoPreviewUrl(`data:${contentType};base64,${base64}`)
    } catch (error) {
      showError('Upload Failed', 'Failed to process logo image')
    } finally {
      setIsUploadingLogo(false)
    }
  }

  // Handle logo removal
  const handleLogoRemove = async () => {
    setLogoBase64(null)
    setLogoFilename(null)
    setLogoContentType(null)
    setLogoPreviewUrl(null)
    setCurrentLogoUrl(null)
    setLogoRemoved(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!partner) {
      showError('Error', 'No sponsor selected')
      return
    }

    if (!name.trim()) {
      showError('Validation Error', 'Sponsor name is required')
      return
    }

    if (name.length > 255) {
      showError('Validation Error', 'Sponsor name must be 255 characters or less')
      return
    }

    if (!categoryId) {
      showError('Validation Error', 'Sponsor category is required')
      return
    }

    if (websiteUrl && !isValidUrl(websiteUrl)) {
      showError('Validation Error', 'Please enter a valid website URL')
      return
    }

    setIsUpdating(true)

    try {
      const updatedPartner = await updatePartner(partner.id, partner.customer_id, {
        name: name.trim(),
        category_id: categoryId,
        tier_id: tierId || null,
        website_url: websiteUrl.trim() || undefined,
        description: description.trim() || undefined,
        status,
        // Include new logo if uploaded
        ...(logoBase64 && logoFilename && logoContentType && {
          logo_base64: logoBase64,
          logo_filename: logoFilename,
          logo_content_type: logoContentType
        }),
        // Set logo_url to null if logo was removed (and no new logo uploaded)
        ...(logoRemoved && !logoBase64 && {
          logo_url: null
        })
      })

      showSuccess('Sponsor Updated', `"${updatedPartner.name}" has been updated successfully`)

      // Reload partners list to get the full partner data with categories and tiers
      await loadPartners(partner.customer_id)

      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError(
        'Failed to update sponsor',
        errorMessage.includes('already exists')
          ? 'A sponsor with this name already exists for this customer'
          : 'Please try again later'
      )
    } finally {
      setIsUpdating(false)
    }
  }

  // Get active categories and tiers
  const activeCategories = categories.filter((c) => c.is_active)
  const activeTiers = tiers.filter((t) => t.is_active)

  if (!partner) return null

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Sponsor"
      description="Update sponsor information"
      icon={<Users className="h-5 w-5" />}
      width="md"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Partner Name */}
        <div>
          <label htmlFor="edit-partner-name" className="block text-xs font-medium text-gray-700 mb-1">
            Sponsor Name <span className="text-red-500">*</span>
          </label>
          <input
            id="edit-partner-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Acme Corporation"
            maxLength={255}
            disabled={isUpdating}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            autoFocus
          />
        </div>

        {/* Logo Upload */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Logo
          </label>
          <ImageUpload
            currentImageUrl={logoPreviewUrl || (currentLogoUrl && partner && selectedCustomer?.id ? getPartnerLogoUrl(partner.id, String(selectedCustomer.id), partner.updated_at) : null)}
            onUpload={handleLogoUpload}
            onRemove={handleLogoRemove}
            accept="image/png,image/jpeg,image/webp"
            maxSizeMB={5}
            disabled={isUpdating}
            isUploading={isUploadingLogo}
          />
          <p className="text-xs text-gray-500 mt-1">
            PNG, JPEG, or WebP up to 5MB. Any aspect ratio supported.
          </p>
        </div>

        {/* Category and Tier Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="edit-category" className="block text-xs font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              id="edit-category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={isUpdating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">Select category</option>
              {activeCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="edit-tier" className="block text-xs font-medium text-gray-700 mb-1">
              Tier
            </label>
            <select
              id="edit-tier"
              value={tierId}
              onChange={(e) => setTierId(e.target.value)}
              disabled={isUpdating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">No tier</option>
              {activeTiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Website URL and Status Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="edit-website-url" className="block text-xs font-medium text-gray-700 mb-1">
              Website URL
            </label>
            <input
              id="edit-website-url"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com"
              disabled={isUpdating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label htmlFor="edit-status" className="block text-xs font-medium text-gray-700 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              id="edit-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as PartnerStatus)}
              disabled={isUpdating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="edit-partner-description" className="block text-xs font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="edit-partner-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a description (optional)"
            rows={2}
            disabled={isUpdating}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isUpdating}
            className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isUpdating || !name.trim() || !categoryId}
            className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Update Sponsor
              </>
            )}
          </button>
        </div>
      </form>
    </Drawer>
  )
}

export default EditPartnerDrawer
